import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import SearchInput from '@components/Shared/SearchInput';
import UserFormModal from '@components/Users/UserFormModal';
import { useLocalization } from '@lib/localization';
import { Add } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, IconButton, LinearProgress, ListItemIcon, Menu, MenuItem, Paper, type Theme } from '@mui/material';
import { DataGrid, type GridColDef, type GridSlots } from '@mui/x-data-grid';
import { useDeleteUserMutation, useGetUsersQuery, type User } from '@store/api/userEndpoints';
import { useAppSelector } from '@store/hooks';
import React, { useState } from 'react';
import { EditIcon, RefreshIcon, TrashIcon } from 'react-line-awesome';

const UsersPage = () => {
  const [search, setSearch] = useState('');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const { data: users, isLoading, refetch, isFetching } = useGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const { t } = useLocalization();
  const { user } = useAppSelector(state => state.auth);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, row: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(row);
  };

  const handleMenuClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    unfocusUser(!!reason);
  };

  const unfocusUser = (cleanSelectedUser: boolean = false) => {
    setAnchorEl(null);
    if (cleanSelectedUser) setSelectedUser(null);
  };

  const handleEdit = () => {
    setFormModal(true);
    unfocusUser();
  };

  const handleDelete = () => {
    setDialogOpen(true);
    unfocusUser();
  };

  const handleUserFormClose = () => {
    setFormModal(false);
    setSelectedUser(null);
  };

  const handleDeleteConfirmation = async () => {
    if (!selectedUser) return;
    await deleteUser(selectedUser.id);
    setSelectedUser(null);
  };

  const handleDeleteCanceled = async () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const columns: GridColDef<User>[] = [
    { field: 'name', headerName: t('Common_Name'), flex: 1 },
    {
      field: 'role',
      flex: 1,
      headerName: t('Common_Role'),
      renderCell: (params) => t(`User_Role_${params.value.capitalize()}`),
    },
    { field: 'username', headerName: t('Common_Username'), flex: 1 },
    {
      field: 'createdAt',
      headerName: t('Common_DateCreated'),
      flex: 1,
      renderCell: (params) => new Date(params.value).toAgoString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => user?.id != params.id ? (
        <IconButton aria-label="more" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ) : null,
    },
  ];

  const rows = users?.filter((user) =>
    user.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={containerStyles}>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          marginBottom: '12px'
        }}
      >
        <SearchInput
          text={search}
          placeholder={t('User_Search')}
          action={setSearch}
          count={rows?.length ?? 0}
        />
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Box>
            <IconButton onClick={refetch} disabled={isLoading || isFetching}>
              <RefreshIcon />
            </IconButton>
          </Box>
          <Box>
            <Button variant="contained" startIcon={<Add />} onClick={() => setFormModal(true)}>
              {t('Common_Create')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Paper sx={{ flexGrow: 1, width: '100%' }}>
        <DataGrid
          loading={isLoading || isFetching}
          slots={{
            loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
            noRowsOverlay: () => <div style={noRowsOverlayStyles}>No users found</div>,
          }}
          rows={rows}
          rowHeight={75}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          disableColumnSelector
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 30]}
          sx={dataGridStyles}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: t('DataGrid_RowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} / ${count}`
            }
          }}
        />
      </Paper>
      <Menu
        id="row-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            sx: {
              padding: 1
            }
          }
        }}>
        <MenuItem
          onClick={handleEdit}
          sx={{ padding: '12px 20px' }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditIcon />
          </ListItemIcon>
          {t('Common_Edit')}
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{ padding: '12px 20px' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <TrashIcon />
          </ListItemIcon>
          {t('Common_Delete')}
        </MenuItem>
      </Menu>
      <UserFormModal isOpen={formModal} onClose={handleUserFormClose} selectedUser={selectedUser} />
      <DeleteConfirmationDialog
        open={dialogOpen}
        onClose={handleDeleteCanceled}
        onConfirm={handleDeleteConfirmation} />
    </div >
  );
};

const containerStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
};

const dataGridStyles = (theme: Theme) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.paper,

  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
    outline: 'none'
  },
});

const noRowsOverlayStyles = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
  fontStyle: 'italic',
};

export default UsersPage;
